import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import dayjs from 'dayjs';
import { Modal } from 'antd';
import { Button } from '@cropler/ui-kit';
import 'react-calendar-timeline/style.css';
import {
  Timeline,
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  CustomMarker,
  TimelineMarkers,
} from 'react-calendar-timeline';

import stylesGantt from '../Gantt/styles.module.scss';
import stylesDevicesDiagram from './styles/devicesDiagram.module.scss';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import { Loading } from '../index.jsx';
import { CustomLabelFormat, CustomTopLabelFormat } from '../Gantt/Gantt.lib.jsx';
import { DateFormat } from '../../constants/data';
import { GanttEditSmallLineButton } from '../Gantt/utils/index.jsx';
import { ModalWidthSmall } from '../../constants/ui';
import { LocationRemoveForm } from './index.jsx';

const DevicesDiagram = ({ t, sensorsData, onClickEditLocation, onAddLocation }) => {
  const today = dayjs().set('hour', 12);
  const defaultTimeStart = dayjs().add(-11, 'month').set('hour', 0).valueOf();
  const defaultTimeEnd = dayjs().add(2, 'month').set('hour', 0).valueOf();

  const { currentUserRole } = useSelector((state) => state.auth);

  const [ganttKey, setUpdateGanttKey] = useState(0);
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [activeRemovePeriodModal, setActiveRemovePeriodModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState({ id: '', name: '' });

  const onOpenDeleteLocationModal = (location) => {
    setLocationToDelete({ id: location.id, name: location.name });
    setActiveRemovePeriodModal(true);
  };

  useEffect(() => {
    if (!sensorsData) return;

    const listItems = [];
    const listGroups = [];

    // Groups (sensors)
    sensorsData.map((item, index) => {
      listGroups.push({
        id: item.id,
        title: (
          <div
            className={`${stylesDevicesDiagram.devicesGanttItem} ${index === sensorsData.length - 1 ? stylesDevicesDiagram.devicesGanttItemLast : ''}`}
          >
            <span>{item.name}</span>
            {!item.location && (
              <Button type={'text'} size={'small'} icon={<CroplerIcon name="add" />} onClick={() => onAddLocation(item)}>
                {t('locations.titleLocation')}
              </Button>
            )}
          </div>
        ),
      });

      // Items (locations)
      const currentLocation = item.location;
      if (currentLocation) {
        listItems.push({
          id: currentLocation.id,
          group: item.id,
          title: (
            <div
              className={`${stylesGantt.ganttLineWrapper} ${stylesGantt.ganttLineWrapperExtended} rct-item-content-item-extended`}
              title=""
            >
              <div
                className={`
                ${stylesGantt.ganttLine} 
                ${stylesGantt.ganttLineExtended} 
                ${currentUserRole === 'owner' || currentUserRole === 'editor' ? stylesGantt.ganttLineExtendedNav : ''}
                `}
              >
                <span className={`${stylesGantt.ganttLineText} ${stylesGantt.ganttLineTextExtended}`}>
                  {currentLocation.name ? currentLocation.name : t('empty.noName')}
                </span>
              </div>
            </div>
          ),
          start_time: dayjs(currentLocation.install_date),
          end_time: dayjs(currentLocation.removal_date),
          canMove: false,
          canResize: false,
        });

        // Edit location button
        if (currentUserRole === 'owner' || currentUserRole === 'editor') {
          listItems.push(
            GanttEditSmallLineButton({
              id: currentLocation.id,
              groupId: item.id,
              endDate: currentLocation.removal_date,
              onClickEdit: () => onClickEditLocation(currentLocation),
              onClickDelete: () => onOpenDeleteLocationModal(currentLocation),
            }),
          );
        }
      }

      if (item.pastLocations && item.pastLocations.length > 0) {
        item.pastLocations.map((location) => {
          listItems.push({
            id: location.id,
            group: item.id,
            title: (
              <div
                className={`
                ${stylesGantt.ganttLineWrapper} 
                ${stylesGantt.ganttLineWrapperExtended} 
                ${currentUserRole === 'owner' || currentUserRole === 'editor' ? stylesGantt.ganttLineExtendedNav : ''} 
                rct-item-content-item-extended1
                `}
                title=""
              >
                <div className={`${stylesGantt.ganttLine} ${stylesGantt.ganttLineExtended}`}>
                  <span className={`${stylesGantt.ganttLineText} ${stylesGantt.ganttLineTextExtended}`}>
                    {location.name ? location.name : t('empty.noName')}
                  </span>
                </div>
              </div>
            ),
            start_time: dayjs(location.install_date),
            end_time: dayjs(location.removal_date),
            canMove: false,
            canResize: false,
          });
          // Edit location button
          if (currentUserRole === 'owner' || currentUserRole === 'editor') {
            listItems.push(
              GanttEditSmallLineButton({
                id: location.id,
                groupId: item.id,
                endDate: location.removal_date,
                onClickDelete: () => onOpenDeleteLocationModal(location),
                onClickEdit: () => onClickEditLocation(location),
              }),
            );
          }
        });
      }
    });

    setGroups(listGroups);
    setItems(listItems);
    setTimeout(() => {
      setUpdateGanttKey(ganttKey + 1);
    }, 11);
  }, [sensorsData]);

  if (!items || !items.length) return <Loading />;

  return (
    <>
      <div className={`${stylesGantt.gantt} ${stylesDevicesDiagram.devicesGantt}`}>
        <Timeline
          key={ganttKey}
          groups={groups}
          items={items}
          sidebarWidth={272}
          lineHeight={48}
          defaultTimeStart={defaultTimeStart}
          defaultTimeEnd={defaultTimeEnd}
          // minZoom={TimeLineMinZoom}
          // maxZoom={TimeLineMaxZoom}
          minResizeWidth={64}
          useResizeHandle={false}
          className="crops"
        >
          <TimelineHeaders calendarHeaderClassName={stylesGantt.ganttTimelineHeaders}>
            <SidebarHeader>
              {() => {
                return (
                  <div
                    className={`${stylesGantt.ganttTimelineSidebarHeader} ${stylesDevicesDiagram.devicesGanttSidebarHeader}`}
                  >
                    <Button className={stylesDevicesDiagram.devicesGanttSort} type={'text'}>
                      {t('sensor.titleSensor')}
                      <span className={stylesDevicesDiagram.devicesGanttSortIcon}>
                        <CroplerIcon name="sortTop" />
                        <CroplerIcon name="sortTop" />
                      </span>
                    </Button>
                  </div>
                );
              }}
            </SidebarHeader>
            <DateHeader unit={'primaryHeader'} labelFormat={CustomTopLabelFormat} height={32} />
            <DateHeader
              unit={'month'}
              labelFormat={CustomLabelFormat}
              height={24}
              intervalRenderer={({ getIntervalProps, intervalContext }) => {
                return (
                  <div
                    {...getIntervalProps()}
                    key={intervalContext.intervalText}
                    className={`${
                      dayjs(intervalContext.interval.startTime).format(DateFormat) === today.format(DateFormat) ||
                      dayjs(intervalContext.interval.startTime).format('MM YY') === today.format('MM YY')
                        ? stylesGantt.ganttDateToday
                        : stylesGantt.ganttDate
                    }`}
                  >
                    <div>{intervalContext.intervalText}</div>
                  </div>
                );
              }}
            />
          </TimelineHeaders>
          <TimelineMarkers>
            <CustomMarker date={today}>
              {({ styles }) => {
                const lineStyles = {
                  ...styles,
                  backgroundColor: 'black',
                  width: '1px',
                  marginLeft: '0',
                };
                return <div style={lineStyles} />;
              }}
            </CustomMarker>
          </TimelineMarkers>
        </Timeline>
      </div>
      {/*Delete Location*/}
      <Modal
        title={t('common.areYouSure')}
        open={activeRemovePeriodModal}
        onCancel={() => setActiveRemovePeriodModal(false)}
        footer={false}
        centered
        width={ModalWidthSmall}
      >
        <LocationRemoveForm location={locationToDelete} closeModal={() => setActiveRemovePeriodModal(false)} t={t} />
      </Modal>
    </>
  );
};

export default DevicesDiagram;
